<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import format from 'date-fns/format';
import TrafficHeatmap from './TrafficHeatmap.vue';

const props = defineProps({
  // { id, name, ai, availability, replies/effective/notes/labels/sends/drafts: [[d, h, count]] }
  agent: { type: Object, required: true },
  days: { type: Array, default: () => [] },
});

const emit = defineEmits(['showMessages']);

const { t } = useI18n();

const ACTION_KEYS = ['replies', 'notes', 'labels', 'sends', 'drafts'];
const todayKey = format(new Date(), 'yyyy-MM-dd');

const sumSeries = series =>
  (series || []).reduce((acc, [, , count]) => acc + Number(count), 0);
const sumToday = series =>
  (series || [])
    .filter(([day]) => day === todayKey)
    .reduce((acc, [, , count]) => acc + Number(count), 0);

const range = computed(() =>
  Object.fromEntries(ACTION_KEYS.map(key => [key, sumSeries(props.agent[key])]))
);
const today = computed(() =>
  Object.fromEntries(ACTION_KEYS.map(key => [key, sumToday(props.agent[key])]))
);

// Replies that carried content — see V2::Reports::EffectiveReplySql.
const effective = computed(() => sumSeries(props.agent.effective));
const effectiveRate = computed(() =>
  range.value.replies
    ? Math.round((effective.value / range.value.replies) * 100)
    : null
);

const autoSendRate = computed(() => {
  const denominator = range.value.replies + range.value.drafts;
  return denominator
    ? Math.round((range.value.replies / denominator) * 100)
    : null;
});

const statsLine = computed(() =>
  props.agent.ai
    ? t('OVERVIEW_V2.AGENTS.AI_STATS', {
        drafts: range.value.drafts,
        notes: range.value.notes,
      })
    : t('OVERVIEW_V2.AGENTS.STATS', {
        notes: range.value.notes,
        labels: range.value.labels,
        sends: range.value.sends,
      })
);

const todayLine = computed(() =>
  props.agent.ai
    ? t('OVERVIEW_V2.AGENTS.AI_TODAY', {
        replies: today.value.replies,
        drafts: today.value.drafts,
        notes: today.value.notes,
      })
    : t('OVERVIEW_V2.AGENTS.TODAY', {
        replies: today.value.replies,
        notes: today.value.notes,
        labels: today.value.labels,
        sends: today.value.sends,
      })
);

const hourActions = computed(() => {
  const hours = Array(24).fill(0);
  ACTION_KEYS.forEach(key => {
    (props.agent[key] || []).forEach(([day, hour, count]) => {
      if (day === todayKey) hours[hour] += Number(count);
    });
  });
  return hours;
});

const pad = hour => String(hour).padStart(2, '0');

const activeLine = computed(() => {
  const active = hourActions.value
    .map((value, hour) => ({ value, hour }))
    .filter(entry => entry.value > 0);
  if (!active.length) return t('OVERVIEW_V2.AGENTS.NO_ACTIVITY');
  return t('OVERVIEW_V2.AGENTS.ACTIVE', {
    from: `${pad(active[0].hour)}:00`,
    to: `${pad(active[active.length - 1].hour + 1)}:00`,
    hours: active.length,
  });
});

const STRIP_CLASSES = [
  'bg-n-blue-5',
  'bg-n-blue-7',
  'bg-n-blue-9',
  'bg-n-blue-11',
];
const EMPTY_STRIP_CLASS =
  'border border-n-container bg-n-slate-2 dark:bg-n-slate-1/30';

const stripMax = computed(() => Math.max(...hourActions.value, 1));

function stripClass(value) {
  if (!value) return EMPTY_STRIP_CLASS;
  const level = Math.min(
    STRIP_CLASSES.length - 1,
    Math.floor((value / stripMax.value) * STRIP_CLASSES.length)
  );
  return STRIP_CLASSES[level];
}

function stripTooltip(hour) {
  const perKey = key =>
    (props.agent[key] || [])
      .filter(([day, rowHour]) => day === todayKey && rowHour === hour)
      .reduce((acc, [, , count]) => acc + Number(count), 0);
  return `${pad(hour)}:00 — ${t('OVERVIEW_V2.AGENTS.STRIP_TOOLTIP', {
    replies: perKey('replies'),
    notes: perKey('notes'),
    labels: perKey('labels'),
    sends: perKey('sends'),
    drafts: perKey('drafts'),
  })}`;
}

const replyCells = computed(() => {
  const cells = {};
  (props.agent.replies || []).forEach(([day, hour, count]) => {
    cells[`${day}|${hour}`] = { value: Number(count), tooltip: `${count}` };
  });
  return cells;
});

const miniDays = computed(() => props.days);

// "Today" stats only make sense when today is part of the fetched range.
const showToday = computed(() => props.days.includes(todayKey));

const initials = computed(() =>
  props.agent.name
    .split(/[\s(]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(word => word[0])
    .join('')
    .toUpperCase()
);

const AVAILABILITY_CLASSES = {
  online: 'bg-n-teal-9',
  busy: 'bg-n-amber-9',
  offline: 'bg-n-slate-8',
};
const availabilityClass = computed(
  () =>
    AVAILABILITY_CLASSES[props.agent.availability] ||
    AVAILABILITY_CLASSES.offline
);
</script>

<template>
  <div
    class="flex flex-col gap-3 rounded-xl p-5 shadow outline outline-1 outline-n-container bg-n-solid-2 cursor-pointer hover:outline-n-slate-6"
    role="button"
    tabindex="0"
    :title="t('OVERVIEW_V2.AGENTS.OPEN_MESSAGES')"
    @click="emit('showMessages', agent)"
    @keydown.enter="emit('showMessages', agent)"
  >
    <div class="flex items-start gap-3">
      <span
        class="flex items-center justify-center w-8 h-8 text-xs font-semibold rounded-full bg-n-slate-4 text-n-slate-12"
      >
        {{ initials }}
      </span>
      <span class="flex items-center gap-2 font-medium text-n-slate-12">
        {{ agent.name }}
        <span
          v-if="agent.ai"
          class="rounded bg-n-blue-5 px-1.5 py-0.5 text-[9px] font-bold text-n-blue-12"
        >
          {{ t('OVERVIEW_V2.AGENTS.AI_BADGE') }}
        </span>
        <span
          class="w-2 h-2 rounded-full"
          :class="availabilityClass"
          :title="agent.availability"
        />
      </span>
      <div class="ml-auto text-right">
        <div class="text-xl font-semibold text-n-slate-12">
          {{ range.replies.toLocaleString() }}
        </div>
        <div class="text-xs text-n-slate-11">
          {{ t('OVERVIEW_V2.AGENTS.REPLIES_RANGE') }}
        </div>
        <div
          v-if="effectiveRate !== null"
          class="text-xs text-n-teal-11"
          :title="t('OVERVIEW_V2.EFFECTIVE_HINT')"
        >
          {{
            t('OVERVIEW_V2.AGENTS.EFFECTIVE', {
              count: effective.toLocaleString(),
              rate: effectiveRate,
            })
          }}
        </div>
        <div class="text-xs text-n-slate-11">{{ statsLine }}</div>
        <div
          v-if="agent.ai && autoSendRate !== null"
          class="text-xs text-n-slate-11"
        >
          {{ t('OVERVIEW_V2.AGENTS.AUTO_SEND_RATE', { rate: autoSendRate }) }}
        </div>
      </div>
    </div>

    <template v-if="showToday">
      <div class="flex flex-wrap gap-x-4 gap-y-1 text-sm text-n-slate-11">
        <span class="text-n-slate-12">{{ todayLine }}</span>
        <span>{{ activeLine }}</span>
      </div>

      <div class="grid grid-cols-[repeat(24,_1fr)] gap-[3px]">
        <div
          v-for="(value, hour) in hourActions"
          :key="hour"
          class="h-3.5 rounded-sm"
          :class="stripClass(value)"
          :title="stripTooltip(hour)"
        />
      </div>
      <div
        class="grid grid-cols-[repeat(24,_1fr)] gap-[3px] text-[7px] font-semibold text-n-slate-11"
      >
        <div v-for="hour in 24" :key="hour" class="text-center">
          {{ (hour - 1) % 6 === 0 ? hour - 1 : '' }}
        </div>
      </div>
    </template>

    <div v-if="miniDays.length > 1">
      <div class="mb-1.5 text-xs text-n-slate-11">
        {{ t('OVERVIEW_V2.AGENTS.REPLY_MAP') }}
      </div>
      <TrafficHeatmap :days="miniDays" :cells="replyCells" mini />
    </div>
  </div>
</template>
